<template>
  <div class="category-configuration">
    <div class="header-section">
      <h1 class="page-title">Categories Setup</h1>
      <p class="page-subtitle">Define and manage categories for traits</p>
    </div>

    <div class="main-content">
      <div class="content-layout">
        <!-- Left Panel - Traits -->
        <div class="left-panel">
          <div class="panel-header">
            <h3 class="panel-title">Select Trait</h3>
            <p class="panel-hint">
              Only traits (leaf nodes) can have categories
            </p>
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input type="text" placeholder="Search Trait..." v-model="traitSearchQuery" />
            </div>
          </div>

          <div class="trait-tree">
            <div v-for="competency in filteredCompetencies" :key="competency.id" class="tree-node">
              <!-- Competency node -->
              <div class="competency-node" :class="{
                'has-children': hasTraits(competency.id),
              }">
                <div class="node-content">
                  <i v-if="hasTraits(competency.id)" class="chevron-icon bi" :class="isExpanded(competency.id)
                    ? 'bi-chevron-down'
                    : 'bi-chevron-right'
                    " @click.stop="toggleExpand(competency.id)">
                  </i>

                  <i class="competency-type-icon bi bi-folder-fill"></i>

                  <span class="competency-name">{{ competency.name }}</span>
                </div>
                <div class="competency-meta">
                  <span class="topics-count parent-count">
                    {{ getTraitCount(competency.id) }} traits
                  </span>
                </div>
              </div>

              <!-- Traits under competency -->
              <div v-if="isExpanded(competency.id)" class="children-container">
                <div v-for="trait in getTraitsForCompetency(competency.id)" :key="trait.id" class="child-wrapper"
                  style="padding-left: 25px">
                  <div class="child-node selectable" :class="{
                    active: selectedTrait?.id === trait.id,
                  }" @click="selectTrait(trait)">
                    <div class="child-content">
                      <div class="node-header">
                        <div class="tree-lines">
                          <div class="vertical-line"></div>
                          <div class="horizontal-line"></div>
                        </div>

                        <!-- <i class="leaf-icon bi bi-circle-fill"></i> -->
                        <!-- <i class="competency-type-icon bi bi-bookmark-fill"></i> -->

                        <span class="child-name">{{ trait.name }}</span>
                      </div>
                      <div class="child-meta">
                        <span class="topics-count selectable-count">
                          {{ getCategoryCount(trait.id) }} categories
                        </span>
                        <span class="weight-pill" :class="getWeightClass(getTotalWeightForTrait(trait.id))
                          ">
                          {{ getTotalWeightForTrait(trait.id) }}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Panel - Categories -->
        <div class="right-panel">
          <div v-if="!selectedTrait" class="empty-state">
            <div class="empty-icon"><i class="bi bi-tags"></i></div>
            <h3>Select a Trait</h3>
            <p>Choose a trait to manage its categories</p>
            <div class="selection-hint">
              <i class="bi bi-info-circle"></i>
              <span>Categories help organize and classify traits</span>
            </div>
          </div>

          <div v-else class="category-section">
            <!-- Category Header -->
            <div class="category-header">
              <div class="header-left">
                <!-- <h3 class="category-title">
                  Categories for {{ selectedTrait.name }}
                </h3> -->

                <h3 class="category-title">Categories</h3>
                <span class="category-count">{{ displayedCategories.length }} categories configured</span>
              </div>
              <div class="header-right">
                <!-- Filter & Actions Section -->
                <div class="filter-actions-container">
                  <button class="filter-btn" @click="toggleFilterPanel" :class="{ active: showFilterPanel }">
                    <i class="bi bi-funnel"></i>
                    Filters
                    <span v-if="selectedForFilter.length > 0" class="filter-count">{{ selectedForFilter.length }}</span>
                  </button>
                </div>

                <div class="search-container">
                  <i class="bi bi-search search-icon"></i>
                  <input type="text" class="search-input" placeholder="Search categories..."
                    v-model="categorySearchQuery" />
                </div>
                <button class="add-btn" @click="openAddCategoryModal">
                  Add Category
                </button>
              </div>
            </div>

            <!-- Filter Panel -->
            <div v-if="showFilterPanel" class="filter-panel">
              <div class="filter-header">
                <h3>Bulk Actions</h3>
                <button class="close-filter-btn" @click="closeFilterPanel">
                  <i class="bi bi-x"></i>
                </button>
              </div>

              <div class="filter-content">
                <div class="filter-section">
                  <h4>Selected Categories ({{ selectedForFilter.length }})</h4>
                  <div class="selected-categories-list">
                    <div v-for="categoryId in selectedForFilter" :key="categoryId" class="selected-category-item">
                      <span>{{ getCategoryName(categoryId) }}</span>
                      <button @click="removeFromFilter(categoryId)" class="remove-btn">
                        <i class="bi bi-x"></i>
                      </button>
                    </div>
                    <div v-if="selectedForFilter.length === 0" class="no-selection">
                      No categories selected. Click checkboxes in the table to
                      select categories.
                    </div>
                  </div>
                </div>

                <div class="filter-actions">
                  <div class="action-group">
                    <label>Apply Equal Weightage:</label>
                    <div class="weight-input-group">
                      <input type="number" v-model.number="bulkWeight" min="1" max="100" placeholder="Weight %"
                        class="weight-input" />
                      <button @click="applyBulkWeight" :disabled="!canApplyBulkWeight" class="apply-btn">
                        Apply
                      </button>
                    </div>
                  </div>

                  <div class="action-group">
                    <label>Bulk Delete:</label>
                    <div class="delete-group">
                      <button @click="bulkDeleteCategories" :disabled="selectedForFilter.length === 0"
                        class="delete-btn-bulk">
                        <i class="bi bi-trash"></i>
                        Delete Selected ({{ selectedForFilter.length }})
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Weight Status Alerts -->
            <div v-if="weightValidationErrors.length > 0" class="weight-alert">
              <i class="bi bi-exclamation-triangle"></i>
              <div>
                <div v-for="error in weightValidationErrors" :key="error" class="weight-error-item">
                  {{ error }}
                </div>
              </div>
            </div>

            <!-- Table Container -->
            <div class="table-container">
              <table class="categories-table" v-if="displayedCategories.length > 0">
                <thead>
                  <tr>
                    <th class="checkbox-col" v-if="showFilterPanel">
                      <input type="checkbox" :checked="allTableItemsSelected" @change="toggleAllTableSelection"
                        class="table-checkbox" />
                    </th>
                    <th class="name-col">Category Name</th>
                    <!-- <th class="desc-col">Description</th> -->
                    <th class="weight-col">Weight (%)</th>
                    <th class="actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(category, index) in displayedCategories" :key="category.id" class="category-row" :class="{
                    'weight-invalid': isWeightInvalid,
                    unconfigured: category.weight === 0,
                  }">
                    <td class="checkbox-col" v-if="showFilterPanel">
                      <input type="checkbox" :checked="selectedForFilter.includes(category.id)"
                        @change="toggleFilterSelection(category.id)" class="table-checkbox" />
                    </td>
                    <td class="name-col">
                      <div class="category-name-container">
                        <span class="category-name">{{ category.name }}</span>
                      </div>
                    </td>
                    <!-- <td class="desc-col">
                      <span class="category-description">{{
                        category.description || "-"
                      }}</span>
                    </td> -->
                    <td class="weight-col">
                      <span class="weight-badge" :class="getCategoryWeightClass(category.weight || 0)">
                        {{ category.weight || 0 }}%
                      </span>
                    </td>
                    <td class="actions-col">
                      <div class="actions-container">
                        <button class="action-btn edit-btn" @click="editCategory(index)" title="Edit">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="action-btn delete-btn" @click="deleteCategory(index)" title="Remove">
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="displayedCategories.length === 0 && !categorySearchQuery" class="table-empty">
                <div class="empty-icon"><i class="bi bi-plus-circle"></i></div>
                <h4>No Categories Added</h4>
                <p>Add categories to this trait to get started</p>
                <button class="add-btn" @click="openAddCategoryModal">
                  Add First Category
                </button>
              </div>

              <div v-if="displayedCategories.length === 0 && categorySearchQuery" class="table-empty">
                <div class="empty-icon"><i class="bi bi-search"></i></div>
                <h4>No Categories Found</h4>
                <p>
                  No categories match your search criteria: "{{
                    categorySearchQuery
                  }}"
                </p>
                <button class="btn-secondary" @click="categorySearchQuery = ''">
                  Clear Search
                </button>
              </div>
            </div>

            <!-- Table Footer with weight status -->
            <div class="table-footer">
              <div class="footer-left">
                <span class="weight-status" :class="weightStatusClass">
                  <i class="bi bi-check-circle-fill"></i>
                  Total Weight for {{ selectedTrait.name }}: {{ totalWeight }}%
                  / 100%
                </span>
              </div>
            </div>

            <!-- Configure Section -->
            <div class="configure-section">
              <button class="configure-btn" @click="configureCategories" :disabled="!allTraitsHaveCategories">
                {{
                  allTraitsHaveCategories
                    ? "Complete Categories Setup"
                    : `Complete All Traits (${incompleteTraitsCount} remaining)`
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Category Modal -->
    <div v-if="showAddCategoryModal" class="modal-overlay" @click="closeAddCategoryModal">
      <div class="add-modal-content" @click.stop>
        <div class="modal-header">
          <h3>Add Category to {{ selectedTrait?.name }}</h3>
          <button class="close-btn" @click="closeAddCategoryModal">
            <i class="bi bi-x"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Search Bar -->
          <div class="search-section">
            <div class="search-container-full">
              <i class="bi bi-search search-icon"></i>
              <input type="text" class="search-input-full" placeholder="Search categories from database..."
                v-model="modalCategorySearch" @input="onSearchInput" @keydown.enter="onSearchEnter" />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button class="create-category-btn" @click="openCreateCategoryModal">
              <i class="bi bi-plus-circle"></i>
              Create Category
            </button>
            <button class="ai-generate-btn" @click="generateCategoriesWithAI">
              <i class="bi bi-magic"></i>
              Generate with AI
            </button>
          </div>

          <!-- Category Cards Grid -->
          <div class="category-grid" v-if="paginatedDatabaseCategories.length > 0">
            <div v-for="category in paginatedDatabaseCategories" :key="category.id" class="category-card"
              :class="{ selected: isCategorySelected(category.id) }" @click="toggleCategorySelection(category.id)">
              <div class="category-info">
                <h4 class="card-title">{{ category.name }}</h4>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredDatabaseCategories.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="bi bi-search"></i>
            </div>
            <template v-if="modalCategorySearch.trim().length > 0">
              <template v-if="!searchFlag && filteredDatabaseCategories.length === 0">
                <h4>Searching for "{{ modalCategorySearch }}"...</h4>
                <p>Please wait while we fetch results.</p>
              </template>

              <template v-if="searchFlag && filteredDatabaseCategories.length === 0">
                <h4>No results for "{{ modalCategorySearch }}"</h4>
                <p>Try a different search term.</p>
              </template>

            </template>

            <template v-else>
              <h4>No Categories Found</h4>
              <p>Try adjusting your search or create a new category
              </p>
            </template>
          </div>

          <!-- Pagination -->
          <div v-if="filteredDatabaseCategories.length > 0" class="pagination-section">
            <div class="pagination-info">
              Showing {{ categoryStartIndex + 1 }}-{{ categoryEndIndex }}
              of {{ totalCategories }} categories
            </div>
            <div class="pagination-controls">
              <button class="pagination-btn" @click="previousCategoryPage" :disabled="categoryCurrentPage === 1">
                <i class="bi bi-chevron-left"></i>
                Previous
              </button>
              <span class="page-indicator">{{ categoryCurrentPage }} of {{ categoryTotalPages }}</span>
              <button class="pagination-btn" @click="nextCategoryPage"
                :disabled="categoryCurrentPage === categoryTotalPages">
                Next
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">
          <div class="footer-left">
            <button class="select-all-btn" @click="toggleSelectAllCategories">
              <i class="bi bi-check2-all"></i>
              {{ allCurrentPageSelected ? "Deselect All" : "Select All" }}
            </button>
            <span class="selection-count">{{ selectedCategories.length }} selected</span>
          </div>
          <div class="footer-right">
            <button class="cancel-btn" @click="closeAddCategoryModal">
              Cancel
            </button>
            <button class="add-selected-btn" @click="addSelectedCategories" :disabled="selectedCategories.length === 0">
              Add Selected ({{ selectedCategories.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit/Create Category Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop ref="modalDialog">
        <div class="modal-header">
          <div class="edit-header-content">
            <h3>{{ editingCategory ? "Edit" : "Create" }} Category</h3>
            <div v-if="editingCategory" class="simple-info">
              <div class="simple-info-line">
                <span class="simple-label">Trait:</span>
                <span class="simple-value">{{
                  selectedTrait?.name || "N/A"
                }}</span>
              </div>
            </div>
          </div>
          <button class="close-btn" @click="closeModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Category Name *</label>
            <input type="text" v-model="categoryForm.name" placeholder="Enter category name" class="modal-input"
              maxlength="100" />
            <div v-if="nameError" class="field-error">{{ nameError }}</div>
          </div>

          <div class="form-group" v-if="editingCategory">
            <label>Weight (%) *</label>
            <input type="number" v-model.number="categoryForm.weight" placeholder="Enter weight" class="modal-input"
              min="1" max="100" />
            <div class="weight-info">
              <span class="current-total">
                Current total for this trait: {{ currentTraitWeight }}%
              </span>
            </div>
            <div v-if="weightError" class="field-error">{{ weightError }}</div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea v-model="categoryForm.description" placeholder="Enter description"
              class="modal-input modal-textarea" rows="3" maxlength="500"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Cancel</button>
          <button class="save-btn" @click="saveCategory" :disabled="!canSave">
            {{ editingCategory ? "Update" : "Create" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <h3>Delete Category</h3>
          <button class="close-btn" @click="closeDeleteModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-content">
            <div class="delete-icon">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div class="delete-message">
              <h4>Are you sure you want to delete this category?</h4>
              <p>
                <strong>"{{ deletingCategory?.name }}"</strong>
              </p>
              <p>
                This action cannot be undone. The category will be permanently
                removed from the trait.
              </p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeDeleteModal">Cancel</button>
          <button class="delete-confirm-btn" @click="confirmDeleteCategory">
            <i class="bi bi-trash"></i>
            Delete Category
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: "CategoriesSetup",
  props: {
    formData: {
      type: Object,
      default: () => ({
        categories: [],
        subcategories: {},
      }),
    },
  },
  emits: ["update-data", "step-completed"],
  data() {
    return {
      selectedTrait: null,
      expandedNodes: [],
      traitSearchQuery: "",
      categorySearchQuery: "",
      modalCategorySearch: "",
      showModal: false,
      showAddCategoryModal: false,
      showDeleteModal: false,
      editingCategory: null,
      deletingCategory: null,
      competencies: [],
      traits: [],

      // Filter Panel
      showFilterPanel: false,
      selectedForFilter: [],
      bulkWeight: null,

      // Add Category Modal Data
      selectedCategories: [],
      categoryCurrentPage: 1,
      categoryItemsPerPage: 10,
      categoryTotalPages: null,
      totalCategories: null,
      searchFlag: false,

      isGeneratingAI: false,

      // Database categories (sample data)
      // databaseCategories: [
      //   {
      //     id: 1,
      //     name: "Assessment & Evaluation",
      //     description:
      //       "Categories related to assessment and evaluation methods",
      //   },
      //   {
      //     id: 2,
      //     name: "Learning & Development",
      //     description:
      //       "Categories focused on learning processes and skill development",
      //   },
      //   {
      //     id: 3,
      //     name: "Technical Skills",
      //     description: "Skill-based categories for technical competencies",
      //   },
      //   {
      //     id: 4,
      //     name: "Knowledge Management",
      //     description: "Knowledge-based categories and information handling",
      //   },
      //   {
      //     id: 5,
      //     name: "Behavioral Competencies",
      //     description: "Behavioral categories and soft skills assessment",
      //   },
      //   {
      //     id: 6,
      //     name: "Communication Skills",
      //     description:
      //       "Communication-related categories and interpersonal skills",
      //   },
      //   {
      //     id: 7,
      //     name: "Problem Solving & Analysis",
      //     description: "Problem-solving and analytical thinking categories",
      //   },
      //   {
      //     id: 8,
      //     name: "Leadership & Management",
      //     description: "Leadership and management competency categories",
      //   },
      //   {
      //     id: 9,
      //     name: "Innovation & Creativity",
      //     description: "Creative thinking and innovation-based categories",
      //   },
      //   {
      //     id: 10,
      //     name: "Teamwork & Collaboration",
      //     description: "Team-based and collaborative work categories",
      //   },
      //   {
      //     id: 11,
      //     name: "Project Management",
      //     description: "Project planning and execution categories",
      //   },
      //   {
      //     id: 12,
      //     name: "Customer Service",
      //     description: "Customer-focused service and relationship categories",
      //   },
      // ],

      databaseCategories: [],

      categories: [],

      categoryForm: {
        name: "",
        description: "",
        weight: null,
      },
    };
  },
  computed: {
    allCurrentPageSelected() {
      return (
        this.paginatedDatabaseCategories.length > 0 &&
        this.paginatedDatabaseCategories.every((category) =>
          this.isCategorySelected(category.id)
        )
      );
    },
    filteredCompetencies() {
      const competenciesWithTraits = this.competencies.filter((comp) =>
        this.traits.some((trait) => trait.competencyId === comp.id)
      );

      if (!this.traitSearchQuery.trim()) {
        return competenciesWithTraits;
      }

      const query = this.traitSearchQuery.toLowerCase().trim();
      return competenciesWithTraits.filter((comp) => {
        const matchesCompetency = comp.name.toLowerCase().includes(query);
        const hasMatchingTrait = this.getTraitsForCompetency(comp.id).some(
          (trait) => trait.name.toLowerCase().includes(query)
        );
        return matchesCompetency || hasMatchingTrait;
      });
    },

    displayedCategories() {
      if (!this.selectedTrait) return [];
      let categories = this.categories.filter(
        (category) => category.traitId === this.selectedTrait.id && category.competencyId === this.selectedTrait.competencyId
      );
      if (this.categorySearchQuery.trim()) {
        const query = this.categorySearchQuery.toLowerCase().trim();
        categories = categories.filter(
          (category) =>
            category.name.toLowerCase().includes(query) ||
            (category.description &&
              category.description.toLowerCase().includes(query))
        );
      }
      return categories;
    },

    // Filter panel computeds
    canApplyBulkWeight() {
      return (
        this.selectedForFilter.length > 0 &&
        this.bulkWeight &&
        this.bulkWeight >= 1 &&
        this.bulkWeight <= 100
      );
    },

    allTableItemsSelected() {
      return (
        this.displayedCategories.length > 0 &&
        this.displayedCategories.every((category) =>
          this.selectedForFilter.includes(category.id)
        )
      );
    },

    // Weight validation computeds
    totalWeight() {
      if (!this.selectedTrait) return 0;
      return this.categories
        .filter((category) => category.traitId === this.selectedTrait.id)
        .reduce((sum, category) => sum + (category.weight || 0), 0);
    },

    currentTraitWeight() {
      return this.totalWeight;
    },

    weightStatusClass() {
      return this.totalWeight === 100 ? "weight-perfect" : "weight-incomplete";
    },

    weightValidationErrors() {
      const errors = [];
      if (this.selectedTrait && this.totalWeight !== 100) {
        errors.push(
          `Categories for "${this.selectedTrait.name}" must total 100% (Current: ${this.totalWeight}%)`
        );
      }
      return errors;
    },

    nameError() {
      return this.categoryForm.name && this.categoryForm.name.length < 2
        ? "Name must be at least 2 characters"
        : null;
    },

    weightError() {
      if (
        this.categoryForm.weight &&
        (this.categoryForm.weight < 1 || this.categoryForm.weight > 100)
      ) {
        return "Weight must be between 1 and 100";
      }
      if (this.willExceedWeight) {
        return `This would exceed 100% (current: ${this.totalWeight}%)`;
      }
      return null;
    },

    willExceedWeight() {
      if (!this.categoryForm.weight || !this.selectedTrait) return false;
      let currentTotal = this.totalWeight;
      if (this.editingCategory) currentTotal -= this.editingCategory.weight;
      return currentTotal + this.categoryForm.weight > 100;
    },

    canSave() {
      if (this.editingCategory) {
        return (
          this.categoryForm.name &&
          this.categoryForm.name.length >= 2 &&
          this.categoryForm.weight &&
          this.categoryForm.weight >= 1 &&
          this.categoryForm.weight <= 100 &&
          !this.willExceedWeight
        );
      } else {
        return this.categoryForm.name && this.categoryForm.name.length >= 2;
      }
    },

    allTraitsHaveCategories() {
      return this.checkAllTraitsHaveCompleteCategories().allComplete;
    },

    incompleteTraitsCount() {
      return this.checkAllTraitsHaveCompleteCategories().incompleteTraits
        .length;
    },

    isWeightInvalid() {
      return this.totalWeight !== 100;
    },

    // Add Category Modal Computed Properties
    filteredDatabaseCategories() {
      if (!this.modalCategorySearch?.trim()) {
        return this.databaseCategories;
      }
      const query = this.modalCategorySearch.toLowerCase().trim();
      if (query.length > 0 && this.searchFlag) {
        return this.databaseCategories.filter(
          (category) =>
            category.name.toLowerCase().includes(query) ||
            category.description.toLowerCase().includes(query)
        );
      } else {
        return []
      }
    },

    // categoryTotalPages() {
    //   return Math.ceil(
    //     this.filteredDatabaseCategories.length / this.categoryItemsPerPage
    //   );
    // },

    categoryStartIndex() {
      return (this.categoryCurrentPage - 1) * this.categoryItemsPerPage;
    },

    categoryEndIndex() {
      return Math.min(this.totalCategories, this.categoryStartIndex + this.categoryItemsPerPage);
    },

    paginatedDatabaseCategories() {
      return this.filteredDatabaseCategories.slice(
        0,
        this.categoryItemsPerPage
      );
    },

    allCategoriesSelected() {
      return this.allCurrentPageSelected;
    },
  },
  async mounted() {
    this.databaseCategories = await this.getCategoryData();
    this.loadData();
    this.autoSelectFirstTrait();
    document.addEventListener("keydown", this.handleEscapeKey);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscapeKey);
  },
  watch: {
    modalCategorySearch() {
      this.categoryCurrentPage = 1;
    },
  },
  methods: {
    // Data loading methods

    ...mapActions([
      "GetCategoryData"]),

    onSearchInput() {
      this.searchFlag = false;
    },

    async onSearchEnter() {
      this.searchFlag = false
      this.currentPage = 1;
      this.databaseCategories = await this.getCategoryData()
      this.searchFlag = true
    },

    async getCategoryData() {
      this.databaseCategories = []
      var response = await this.GetCategoryData({
        pageNumber: this.categoryCurrentPage,
        pageSize: this.categoryItemsPerPage,
        searchStr: this.modalCategorySearch || "",
      });
      const catResponse = response?.object?.data
      if (catResponse) {
        this.categoryTotalPages = catResponse?.totalPages
        this.categoryCurrentPage = catResponse.currentPage
        this.totalCategories = catResponse.totalItems
        return catResponse?.categories.map(item => {
          return {
            id: item.qcid,
            name: item.categoryName,
            description: '',
            competencyId: null,
            traitid: null
          }
        })
      }
      return []
    },

    saveToLocalStorage() {
      try {
        const data = {
          categories: this.categories,
          databaseCategories: this.databaseCategories,
        };
        localStorage.setItem("categoriesSetup", JSON.stringify(data));
        console.log("✅ Categories saved to localStorage.");
      } catch (error) {
        console.warn("❌ Failed to save categories to localStorage:", error);
      }
    },

    loadData() {
      console.log("🔄 Loading trait configuration data...");

      try {
        const traitConfig = localStorage.getItem("traitConfiguration");
        if (traitConfig) {
          const data = JSON.parse(traitConfig);
          this.competencies = data.competencies || [];
          this.traits = data.traits || [];
          console.log("✅ Loaded trait configuration:", {
            competencies: this.competencies.length,
            traits: this.traits.length,
          });
        }
      } catch (error) {
        console.warn("Error loading trait configuration:", error);
      }

      // Load existing categories and database categories if any
      try {
        const categoriesConfig = localStorage.getItem("categoriesSetup");
        if (categoriesConfig) {
          const data = JSON.parse(categoriesConfig);
          this.categories = data.categories || [];
          // Merge with existing database categories, avoiding duplicates
          if (data.databaseCategories) {
            const existingIds = this.databaseCategories.map((cat) => cat.id);
            const newCategories = data.databaseCategories.filter(
              (cat) => !existingIds.includes(cat.id)
            );
            this.databaseCategories = [
              ...newCategories,
              ...this.databaseCategories,
            ];
          }
          console.log("✅ Loaded categories:", this.categories.length);
        }
      } catch (error) {
        console.warn("Error loading categories configuration:", error);
      }

      // Auto-expand all competencies that have traits
      this.expandedNodes = this.competencies
        .filter((comp) => this.hasTraits(comp.id))
        .map((comp) => comp.id);
    },

    // Tree navigation methods
    hasTraits(competencyId) {
      return this.traits.some((trait) => trait.competencyId === competencyId);
    },

    getTraitsForCompetency(competencyId) {
      return this.traits.filter((trait) => trait.competencyId === competencyId);
    },

    getTraitCount(competencyId) {
      return this.getTraitsForCompetency(competencyId).length;
    },

    getCategoryCount(traitId) {
      return this.categories.filter((cat) => cat.traitId === traitId).length;
    },

    getTotalWeightForTrait(traitId) {
      const traitCategories = this.categories.filter(
        (cat) => cat.traitId === traitId
      );
      return traitCategories.reduce(
        (sum, category) => sum + (category.weight || 0),
        0
      );
    },

    getWeightClass(weight) {
      if (weight === 100) return "weight-perfect";
      if (weight >= 70) return "weight-high";
      if (weight >= 40) return "weight-medium";
      if (weight > 0) return "weight-low";
      return "weight-none";
    },

    getCategoryWeightClass(weight) {
      return weight >= 50
        ? "weight-high"
        : weight >= 30
          ? "weight-medium"
          : weight > 0
            ? "weight-low"
            : "weight-none";
    },

    isExpanded(competencyId) {
      return this.expandedNodes.includes(competencyId);
    },

    toggleExpand(competencyId) {
      const index = this.expandedNodes.indexOf(competencyId);
      if (index > -1) {
        this.expandedNodes.splice(index, 1);
      } else {
        this.expandedNodes.push(competencyId);
      }
    },

    selectTrait(trait) {
      this.selectedTrait = trait;
      this.selectedForFilter = [];
      this.showNotification(`Selected trait: ${trait.name}`, "success");
    },

    autoSelectFirstTrait() {
      if (this.traits.length > 0) {
        this.selectedTrait = this.traits[0];
        console.log("✅ Auto-selected first trait:", this.selectedTrait.name);
      }
    },

    handleEscapeKey(event) {
      if (event.key === "Escape") {
        if (this.showModal) {
          this.closeModal();
        } else if (this.showDeleteModal) {
          this.closeDeleteModal();
        } else if (this.showAddCategoryModal) {
          this.closeAddCategoryModal();
        }
      }
    },

    // Filter panel methods
    toggleFilterPanel() {
      this.showFilterPanel = !this.showFilterPanel;
    },

    closeFilterPanel() {
      this.showFilterPanel = false;
      this.selectedForFilter = [];
    },

    toggleFilterSelection(categoryId) {
      const index = this.selectedForFilter.indexOf(categoryId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      } else {
        this.selectedForFilter.push(categoryId);
      }
    },

    toggleAllTableSelection() {
      if (this.allTableItemsSelected) {
        this.selectedForFilter = [];
      } else {
        this.selectedForFilter = [...this.displayedCategories.map((c) => c.id)];
      }
    },

    removeFromFilter(categoryId) {
      const index = this.selectedForFilter.indexOf(categoryId);
      if (index > -1) {
        this.selectedForFilter.splice(index, 1);
      }
    },

    getCategoryName(categoryId) {
      const category = this.categories.find((c) => c.id === categoryId);
      return category ? category.name : "Unknown";
    },

    // Bulk operations
    applyBulkWeight() {
      if (!this.canApplyBulkWeight) return;

      this.selectedForFilter.forEach((categoryId) => {
        const index = this.categories.findIndex(
          (c) => c.id === categoryId && c.traitId === this.selectedTrait.id && c.competencyId === this.selectedTrait.competencyId
        );
        if (index > -1) {
          this.categories[index] = {
            ...this.categories[index],
            weight: this.bulkWeight,
          };
        }
      });

      this.showNotification(
        `Applied ${this.bulkWeight}% weight to ${this.selectedForFilter.length} categories`,
        "success"
      );
      this.selectedForFilter = [];
      this.bulkWeight = null;
    },

    bulkDeleteCategories() {
      if (this.selectedForFilter.length === 0) return;

      if (
        confirm(
          `Are you sure you want to delete ${this.selectedForFilter.length} selected categories? This action cannot be undone.`
        )
      ) {
        this.categories = this.categories.filter(
          (category) =>
            !(
              this.selectedForFilter.includes(category.id) && 
              category.traitId === this.selectedTrait.id && category.competencyId === this.selectedTrait.competencyId
            )
        );
        this.showNotification(
          `Deleted ${this.selectedForFilter.length} categories`,
          "warning"
        );
        this.selectedForFilter = [];
      }
    },

    // Add Category Modal methods
    openAddCategoryModal() {
      if (!this.selectedTrait) {
        this.showNotification("Please select a trait first", "warning");
        return;
      }
      this.showAddCategoryModal = true;
      this.selectedCategories = [];
      this.modalCategorySearch = "";
      this.categoryCurrentPage = 1;

      // Refresh database categories to include any newly created ones
      this.refreshDatabaseCategories();
    },

    closeAddCategoryModal() {
      this.showAddCategoryModal = false;
      this.selectedCategories = [];
      this.modalCategorySearch = "";
      this.categoryCurrentPage = 1;
    },

    refreshDatabaseCategories() {
      // This method ensures newly created categories appear in the modal
      // The databaseCategories array is already updated in saveCategory method
      this.$forceUpdate();
    },

    isCategorySelected(categoryId) {
      return this.selectedCategories.includes(categoryId);
    },

    toggleCategorySelection(categoryId) {
      const index = this.selectedCategories.indexOf(categoryId);
      if (index > -1) {
        this.selectedCategories.splice(index, 1);
      } else {
        this.selectedCategories.push(categoryId);
      }
    },

    toggleSelectAllCategories() {
      if (this.allCurrentPageSelected) {
        // Deselect all on current page
        this.paginatedDatabaseCategories.forEach((category) => {
          const index = this.selectedCategories.indexOf(category.id);
          if (index > -1) {
            this.selectedCategories.splice(index, 1);
          }
        });
      } else {
        // Select all on current page
        this.paginatedDatabaseCategories.forEach((category) => {
          if (!this.isCategorySelected(category.id)) {
            this.selectedCategories.push(category.id);
          }
        });
      }
    },

    async previousCategoryPage() {
      if (this.categoryCurrentPage > 1) {
        this.categoryCurrentPage--;
        this.databaseCategories = await this.getCategoryData();
      }
    },

    async nextCategoryPage() {
      if (this.categoryCurrentPage < this.categoryTotalPages) {
        this.categoryCurrentPage++;
        this.databaseCategories = [];
        const data = await this.getCategoryData();
        this.databaseCategories = data;
      }
    },

    generateCategoriesWithAI() {
      this.showNotification("AI trait generation feature coming soon!", "info");
    },

    openCreateCategoryModal() {
      this.editingCategory = null;
      this.resetForm();
      this.showModal = true;
      // Don't close the add modal - keep it open in background
      // this.showAddCategoryModal will remain true
    },

    addSelectedCategories() {
      if (this.selectedCategories.length === 0) {
        this.showNotification("Please select at least one category", "warning");
        return;
      }

      const selectedDbCategories = this.databaseCategories.filter((category) =>
        this.selectedCategories.includes(category.id)
      );

      // Check for duplicates
      const existingCategoryNames = this.categories
        .filter((cat) => cat.traitId === this.selectedTrait.id)
        .map((cat) => cat.name.toLowerCase());

      var categoriesToAdd = selectedDbCategories.filter(
        (dbCategory) =>
          !existingCategoryNames.includes(dbCategory.name.toLowerCase())
      );

      categoriesToAdd = categoriesToAdd.map(item => ({
        ...item,
        traitId: this.selectedTrait.id,
        competencyId: this.selectedTrait.competencyId
      }));


      if (categoriesToAdd.length === 0) {
        this.showNotification(
          "Selected categories already exist for this trait",
          "warning"
        );
        return;
      }

      categoriesToAdd.forEach((dbCategory) => {
        const newCategory = {
          id: dbCategory.id,
          traitId: this.selectedTrait.id,
          competencyId: this.selectedTrait.competencyId,
          name: dbCategory.name,
          description: dbCategory.description,
          weight: 0, // Default weight is 0 - needs to be configured
          dateAdded: new Date().toISOString(),
        };
        this.categories.push(newCategory);
      });

      this.closeAddCategoryModal();
      this.showNotification(
        `${categoriesToAdd.length} categories added successfully! Please use the Edit button to configure weights.`,
        "success"
      );

      // Auto-save to localStorage
      this.saveToLocalStorage();
    },

    // CRUD methods
    editCategory(index) {
      if (!this.selectedTrait) {
        this.showNotification("Please select a trait first", "warning");
        return;
      }
      const category = this.displayedCategories[index];
      const actualIndex = this.categories.findIndex(
        (c) => c.id === category.id && c.traitId === this.selectedTrait.id
      );

      this.editingCategory = this.categories[actualIndex];
      this.categoryForm = {
        name: category.name,
        description: category.description || "",
        weight: category.weight || 0,
      };
      this.showModal = true;

      this.$nextTick(() => {
        const firstInput =
          this.$refs.modalDialog?.querySelector('input[type="text"]');
        if (firstInput) firstInput.focus();
      });
    },

    deleteCategory(index) {
      const category = this.displayedCategories[index];
      this.deletingCategory = category;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deletingCategory = null;
    },

    confirmDeleteCategory() {
      if (!this.deletingCategory) return;

      const index = this.categories.findIndex(
        (c) =>
          c.id === this.deletingCategory.id &&
          c.traitId === this.selectedTrait.id
      );
      if (index > -1) {
        this.categories.splice(index, 1);
        this.showNotification(
          `Category "${this.deletingCategory.name}" deleted successfully`,
          "success"
        );
      }

      this.closeDeleteModal();
    },

    closeModal() {
      this.showModal = false;
      this.editingCategory = null;
      this.resetForm();
    },

    resetForm() {
      this.categoryForm = {
        name: "",
        description: "",
        weight: null,
      };
    },

    saveCategory() {
      if (!this.canSave) {
        this.showNotification(
          "Please fill all required fields correctly",
          "error"
        );
        return;
      }

      if (this.editingCategory) {
        // Editing existing category
        const category = {
          id: this.editingCategory.id,
          traitId: this.selectedTrait.id,
          competencyId: this.selectedTrait.competencyId,
          name: this.categoryForm.name.trim(),
          description: this.categoryForm.description.trim(),
          weight: this.categoryForm.weight,
          dateModified: new Date().toISOString(),
        };

        const index = this.categories.findIndex(
          (c) =>
            c.id === this.editingCategory.id &&
            c.traitId === this.selectedTrait.id && c.competencyId === this.selectedTrait.competencyId
        );
        if (index > -1) {
          this.categories.splice(index, 1, category);
          this.showNotification("Category updated successfully", "success");
        }
      } else {
        // Creating new category - add to database categories list
        const newDatabaseCategory = {
          id: `db_cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: this.categoryForm.name.trim(),
          description: this.categoryForm.description.trim(),
          isCustom: true,
          dateCreated: new Date().toISOString(),
        };

        // Add to database categories
        this.databaseCategories.unshift(newDatabaseCategory); // Add to beginning

        this.showNotification(
          `New category "${newDatabaseCategory.name}" created successfully! It's now available in the category list.`,
          "success"
        );

        // If we're in the add modal, auto-select the new category
        if (this.showAddCategoryModal) {
          this.selectedCategories.push(newDatabaseCategory.id);
          this.categoryCurrentPage = 1; // Go to first page to see the new category
          this.modalCategorySearch = ""; // Clear search to show all categories
        }
      }

      this.closeModal();
      this.saveToLocalStorage();
    },
    // Configuration completion
    configureCategories() {
      const traitCategoryCheck = this.checkAllTraitsHaveCompleteCategories();

      if (!traitCategoryCheck.allComplete) {
        const incompleteList = traitCategoryCheck.incompleteTraits
          .map((trait) => `${trait.name} (${trait.currentWeight}%)`)
          .join(", ");

        this.showNotification(
          `Each trait must have 100% category weight allocation before proceeding.\n\nIncomplete traits:\n${incompleteList}`,
          "error"
        );
        return;
      }

      const categoryData = {
        categories: this.categories,
      };

      this.$emit("update-data", {
        stepKey: "categoriesSetup",
        data: categoryData,
      });

      localStorage.setItem(
        "categoriesSetup",
        JSON.stringify({
          traits: this.traits,
          categories: this.categories,
        })
      );

      this.showNotification(
        "Categories Setup completed! All traits have 100% category weight allocation.",
        "success"
      );
      this.$emit("step-completed", 4);
    },

    checkAllTraitsHaveCompleteCategories() {
      const incompleteTraits = [];

      for (const trait of this.traits) {
        const traitCategories = this.categories.filter(
          (cat) => cat.traitId === trait.id
        );

        const totalWeight = traitCategories.reduce(
          (sum, category) => sum + (category.weight || 0),
          0
        );

        if (totalWeight !== 100) {
          incompleteTraits.push({
            ...trait,
            currentWeight: totalWeight,
            missingWeight: 100 - totalWeight,
            categoryCount: traitCategories.length,
          });
        }
      }

      return {
        allComplete: incompleteTraits.length === 0,
        incompleteTraits,
      };
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          info: "alert-info",
          warning: "alert-warning",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    },
  },
};
</script>

<style scoped>
.category-configuration {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.main-content {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.content-layout {
  display: grid;
  grid-template-columns: 350px 1fr;
  min-height: 70vh;
}

/* Left Panel - Traits Tree */
.left-panel {
  background-color: #fafafa;
  border-right: 1px solid #e0e0e0;
}

.panel-header {
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
}

.panel-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.panel-hint {
  font-size: 0.75rem;
  color: #718096;
  margin-bottom: 1rem;
  font-style: italic;
  background: #e6fffa;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #38b2ac;
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  outline: none;
  background-color: white;
  transition: all 0.2s ease;
}

.search-box input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.trait-tree {
  padding: 1rem 0;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

/* Competency Nodes */
.tree-node {
  margin-bottom: 0.5rem;
}

.competency-node {
  display: flex;
  flex-direction: column;
  margin: 0.5rem 1rem;
  cursor: default;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
}

.competency-node.has-children:hover {
  background-color: #f7fafc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.node-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
}

.competency-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem 0.75rem 1rem;
  margin-top: -0.25rem;
}

.chevron-icon {
  margin-right: 0.75rem;
  font-size: 0.875rem;
  color: #718096;
  transition: all 0.2s ease;
  width: 16px;
  text-align: center;
  cursor: pointer;
}

.chevron-icon:hover {
  color: #8b5cf6;
}

.competency-type-icon {
  margin-right: 0.5rem;
  font-size: 0.875rem;
  color: #805ad5;
  opacity: 0.8;
}

.competency-name {
  font-weight: 600;
  font-size: 0.95rem;
  flex: 1;
  color: #4a5568;
}

.parent-count {
  color: #805ad5;
  font-weight: 500;
  font-size: 0.75rem;
}

.topics-count {
  font-size: 0.75rem;
  color: #718096;
  background-color: #edf2f7;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

/* Child Nodes (Traits) */
.children-container {
  position: relative;
  margin-left: 1rem;
}

.children-container::before {
  content: "";
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e2e8f0;
}

.child-wrapper {
  position: relative;
}

.child-node {
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
  margin-right: 16px;
}

.child-node.selectable:hover {
  background-color: #f0fff4;
  border-color: #48bb78;
  transform: translateX(2px);
}

.child-node.selectable.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
  border-color: #38a169;
}

.child-content {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
}

.node-header {
  display: flex;
  align-items: center;
  width: 100%;
}

.child-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

.tree-lines {
  position: relative;
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.vertical-line {
  position: absolute;
  left: 10px;
  top: -10px;
  width: 1px;
  height: 30px;
  background: #e2e8f0;
}

.horizontal-line {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 10px;
  height: 1px;
  background: #e2e8f0;
}

.leaf-icon {
  color: #48bb78;
  font-size: 0.625rem;
  margin-right: 0.5rem;
}

.child-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #4a5568;
  flex: 1;
}

.child-node.active .child-name {
  color: white;
}

.selectable-count {
  color: #22543d;
  font-weight: 500;
  background-color: #c6f6d5;
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  font-size: 0.7rem;
}

.child-node.active .topics-count,
.child-node.active .selectable-count {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Weight Pills */
.weight-pill {
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  margin-left: 0.5rem;
}

/* .weight-perfect {
  background-color: #48bb78;
} */

.weight-high {
  background-color: #ed8936;
}

.weight-medium {
  background-color: #f6e05e;
  color: #744210;
}

.weight-low {
  background-color: #fc8181;
}

.weight-none {
  background-color: #cbd5e0;
  color: #4a5568;
}

/* Right Panel - Categories */
.right-panel {
  background-color: white;
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-state .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state p {
  font-size: 1rem;
  color: #718096;
}

.selection-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e6fffa;
  border-radius: 6px;
  color: #22543d;
  font-size: 0.875rem;
}

.selection-hint i {
  color: #38b2ac;
}

.category-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #fafafa;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.category-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.category-count {
  font-size: 0.875rem;
  color: #718096;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Filter & Actions */
.filter-actions-container {
  position: relative;
}

.filter-btn {
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-btn:hover,
.filter-btn.active {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.filter-count {
  background: #e53e3e;
  color: white;
  border-radius: 10px;
  padding: 0.125rem 0.375rem;
  font-size: 0.75rem;
  min-width: 1.25rem;
  text-align: center;
}

.search-container {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-input {
  padding: 0.5rem 0.75rem 0.5rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
  width: 250px;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.add-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-btn:hover {
  background: #7c3aed;
}

/* Filter Panel */
.filter-panel {
  background: #f8f9fa;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin: 1rem 2rem;
  overflow: hidden;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.filter-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
}

.close-filter-btn {
  background: none;
  border: none;
  color: #718096;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-filter-btn:hover {
  background: #f7fafc;
  color: #2d3748;
}

.filter-content {
  padding: 1.5rem;
}

.filter-section {
  margin-bottom: 1.5rem;
}

.filter-section h4 {
  margin: 0 0 0.75rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.selected-categories-list {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.75rem;
  max-height: 120px;
  overflow-y: auto;
}

.selected-category-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: #f7fafc;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.selected-category-item:last-child {
  margin-bottom: 0;
}

.remove-btn {
  background: none;
  border: none;
  color: #e53e3e;
  cursor: pointer;
  padding: 0.125rem;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #fed7d7;
}

.no-selection {
  color: #718096;
  font-style: italic;
  text-align: center;
  padding: 1rem;
}

.filter-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
}

.weight-input-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.weight-input {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  width: 100px;
  font-size: 0.875rem;
  outline: none;
}

.weight-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.apply-btn {
  background: #48bb78;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.apply-btn:hover:not(:disabled) {
  background: #38a169;
}

.apply-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

.delete-group {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.delete-btn-bulk {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delete-btn-bulk:hover:not(:disabled) {
  background: #c53030;
}

.delete-btn-bulk:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Weight Alerts */
.weight-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 2rem;
  padding: 1rem;
  background: #fed7d7;
  border: 1px solid #fc8181;
  border-radius: 8px;
  color: #742a2a;
}

.weight-alert i {
  color: #e53e3e;
  font-size: 1.25rem;
  margin-top: 0.125rem;
}

.weight-error-item {
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.25rem;
}

.weight-error-item:last-child {
  margin-bottom: 0;
}

/* Table Styles */
.table-container {
  flex: 1;
  overflow-x: auto;
  background: white;
  padding: 1rem 2rem;
}

.categories-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.categories-table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.categories-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.category-row {
  transition: all 0.2s ease;
}

.category-row:hover {
  background-color: #f7fafc;
}

.category-row.unconfigured {
  background-color: #fef5e7;
}

.category-row.weight-invalid {
  background-color: #fed7d7;
}

.checkbox-col {
  width: 50px;
  text-align: center;
}

.table-checkbox {
  transform: scale(1.1);
  cursor: pointer;
}

.name-col {
  min-width: 200px;
}

.desc-col {
  min-width: 250px;
  max-width: 300px;
}

.weight-col {
  width: 120px;
  text-align: center;
}

.actions-col {
  width: 150px;
  text-align: center;
}

.category-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.category-name {
  font-weight: 500;
  color: #2d3748;
}

.category-description {
  color: #718096;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.weight-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.weight-badge.weight-high {
  background-color: #48bb78;
}

.weight-badge.weight-medium {
  background-color: #ed8936;
}

.weight-badge.weight-low {
  background-color: #fc8181;
}

.weight-badge.weight-none {
  background-color: #cbd5e0;
  color: #4a5568;
}

.actions-container {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  color: #718096;
  padding: 0.375rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.edit-btn:hover {
  background: #48bb78;
  color: white;
  border-color: #48bb78;
}

.delete-btn:hover {
  background: #e53e3e;
  color: white;
  border-color: #e53e3e;
}

.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.table-empty .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.table-empty h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.table-empty p {
  font-size: 1rem;
  color: #718096;
  margin-bottom: 1.5rem;
}

.btn-secondary {
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: #cbd5e0;
}

/* Table Footer */
.table-footer {
  padding: 1rem 2rem;
  border-top: 1px solid #e2e8f0;
  background: #fafafa;
}

.footer-left {
  display: flex;
  align-items: center;
}

.weight-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.weight-status.weight-perfect {
  color: #22543d;
}

.weight-status.weight-perfect i {
  color: #48bb78;
}

.weight-status.weight-incomplete {
  color: #c53030;
}

.weight-status.weight-incomplete i {
  color: #e53e3e;
}

.weight-info {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #718096;
}

.current-total {
  font-weight: 500;
}

/* Configure Section */
.configure-section {
  padding: 1.5rem 2rem;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.configure-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.configure-btn:hover:not(:disabled) {
  background: #7c3aed;
}

.configure-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.add-modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: #fafafa;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
}

.edit-header-content {
  flex: 1;
}

.simple-info {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #718096;
}

.simple-info-line {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.simple-info-line:last-child {
  margin-bottom: 0;
}

.simple-label {
  font-weight: 500;
}

.simple-value {
  color: #2d3748;
}

.close-btn {
  background: none;
  border: none;
  color: #718096;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f7fafc;
  color: #2d3748;
}

.modal-body {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #4a5568;
  font-size: 0.875rem;
}

.modal-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
  transition: all 0.2s ease;
}

.modal-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.modal-textarea {
  resize: vertical;
  min-height: 80px;
}

.field-error {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #e53e3e;
}

/* Search Section in Modal */
.search-section {
  margin-bottom: 1.5rem;
}

.search-container-full {
  position: relative;
}

.search-input-full {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
}

.search-input-full:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

/* Action Buttons in Modal */
.action-buttons {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.create-category-btn,
.ai-generate-btn {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #48bb78;
  color: white;
}

.create-category-btn:hover {
  background: #38a169;
}

.ai-generate-btn {
  background: #8b5cf6;
  color: white;
}

.ai-generate-btn:hover {
  background: #7c3aed;
}

/* Category Cards Grid */
.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.category-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
}

.category-card:hover {
  border-color: #8b5cf6;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
  transform: translateY(-2px);
}

.category-card.selected {
  border-color: #8b5cf6;
  background: #f7fafc;
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
}

.category-info {
  text-align: center;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.3;
  margin: 0 0 0.5rem 0;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.card-description {
  font-size: 0.8rem;
  color: #718096;
  line-height: 1.4;
  margin: 0;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 1px solid #e2e8f0;
}

.pagination-info {
  font-size: 0.875rem;
  color: #718096;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.pagination-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-btn:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #8b5cf6;
}

.pagination-btn:disabled {
  background: #f7fafc;
  color: #a0aec0;
  cursor: not-allowed;
}

.page-indicator {
  font-size: 0.875rem;
  color: #4a5568;
  font-weight: 500;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #fafafa;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.footer-right {
  display: flex;
  gap: 0.75rem;
}

.select-all-btn {
  background: none;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.select-all-btn:hover {
  background: #f7fafc;
  border-color: #8b5cf6;
}

.selection-count {
  font-size: 0.875rem;
  color: #718096;
}

.cancel-btn {
  background: white;
  border: 1px solid #e2e8f0;
  color: #4a5568;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.add-selected-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-selected-btn:hover:not(:disabled) {
  background: #7c3aed;
}

.add-selected-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

.save-btn {
  background: #48bb78;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.save-btn:hover:not(:disabled) {
  background: #38a169;
}

.save-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
}

/* Delete Modal Styles */
.delete-content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.delete-icon {
  color: #e53e3e;
  font-size: 2rem;
}

.delete-message h4 {
  margin: 0 0 0.5rem 0;
  color: #2d3748;
  font-size: 1.125rem;
}

.delete-message p {
  margin: 0 0 0.5rem 0;
  color: #718096;
  line-height: 1.4;
}

.delete-confirm-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delete-confirm-btn:hover {
  background: #c53030;
}

/* Responsive Design */
@media (max-width: 768px) {
  .content-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .left-panel {
    max-height: 300px;
    overflow-y: auto;
  }

  .category-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .header-right {
    flex-wrap: wrap;
  }

  .category-grid {
    grid-template-columns: 1fr;
  }

  .action-buttons {
    flex-direction: column;
  }

  .modal-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .footer-left,
  .footer-right {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .category-configuration {
    padding: 1rem;
  }

  .modal-content,
  .add-modal-content {
    width: 95%;
    margin: 0.5rem;
  }

  .categories-table {
    font-size: 0.75rem;
  }

  .categories-table th,
  .categories-table td {
    padding: 0.5rem;
  }

  .category-card {
    width: 100%;
    height: 100px;
  }
}

/* Custom scrollbar for tree and modals */
.trait-tree::-webkit-scrollbar,
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.trait-tree::-webkit-scrollbar-track,
.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.trait-tree::-webkit-scrollbar-thumb,
.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.trait-tree::-webkit-scrollbar-thumb:hover,
.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Focus states for accessibility */
.category-card:focus,
.competency-node:focus,
.child-node:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.pagination-btn:focus,
.action-btn:focus,
.add-btn:focus,
.filter-btn:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.modal-input:focus {
  outline: none;
}

/* Print styles */
@media print {

  .modal-overlay,
  .action-btn,
  .add-btn,
  .configure-btn {
    display: none !important;
  }
}
</style>
